import type { PackageStatus } from '../../types/packages';
import { packageStatusChipClass } from './packageUtils';
import type { PackagesT } from './packagesI18n';

export type PackageTransition = 'withdraw' | 'pause' | 'resume' | 'end';
export type PackageEditorBusy = null | 'save' | 'submit' | PackageTransition;

export interface PackageEditorHeaderProps {
  t: PackagesT;
  lang: 'en' | 'ar';
  title: string;
  status: PackageStatus | null;
  busy: PackageEditorBusy;
  /** Save is offered (not read-only). */
  canSave: boolean;
  saveLabel: string;
  /** Submit is offered (new, DRAFT, REJECTED, SUSPENDED). */
  canSubmit: boolean;
  /** Fewer than 2 services: Submit shows but is disabled with the hint. */
  submitBlocked: boolean;
  /** Lifecycle actions for the current status (each confirmed in a dialog by the page). */
  transitions: PackageTransition[];
  onBack: () => void;
  onSave: () => void;
  onSubmit: () => void;
  onTransition: (action: PackageTransition) => void;
}

/** Spec 081b — back link, title + status chip, and the version-aware actions. */
export default function PackageEditorHeader(props: PackageEditorHeaderProps) {
  const { t, lang, title, status, busy, canSave, saveLabel, canSubmit, submitBlocked, transitions, onBack, onSave, onSubmit, onTransition } = props;
  return (
    <div className="pkg-editor__header">
      <button type="button" className="offer-back" onClick={onBack}>
        <span aria-hidden="true">{lang === 'ar' ? '→' : '←'}</span> {t('packages.editor.back')}
      </button>
      <div className="pkg-editor__head-row">
        <div className="offer-editor__head">
          <h2>{title}</h2>
          {status && <span className={packageStatusChipClass(status)} data-testid="pkg-status">{t(`packages.status.${status}`)}</span>}
        </div>
        <div className="pkg-editor__actions">
          {transitions.map((a) => (
            <button key={a} type="button" disabled={!!busy}
              className={`offer-btn ${a === 'end' || a === 'withdraw' ? 'offer-btn--danger-ghost' : 'offer-btn--ghost'}`}
              onClick={() => onTransition(a)}>
              {t(`packages.action.${a}`)}
            </button>
          ))}
          {canSave && (
            <button type="button" className="offer-btn offer-btn--ghost" onClick={onSave} disabled={!!busy}>
              {busy === 'save' ? t('packages.bar.saving') : saveLabel}
            </button>
          )}
          {canSubmit && (
            <button type="button" className="offer-btn offer-btn--primary" onClick={onSubmit} disabled={!!busy || submitBlocked}
              aria-describedby={submitBlocked ? 'pkg-submit-hint' : undefined}>
              {busy === 'submit' ? t('packages.bar.saving') : t('packages.bar.submit')}
            </button>
          )}
        </div>
      </div>
      {canSubmit && submitBlocked && <p className="offer-field-hint pkg-editor__submit-hint" id="pkg-submit-hint">{t('packages.bar.submitNeedsItems')}</p>}
    </div>
  );
}
